import axios from 'axios';

export interface JNImage {
  jnid: string;
  url: string;
}

export class ImagesService {
  private engageOrigin: string;
  private api1Origin: string;
  private accessToken: string;

  constructor(engageOrigin: string, api1Origin: string, accessToken: string) {
    this.engageOrigin = engageOrigin;
    this.api1Origin = api1Origin;
    this.accessToken = accessToken;
  }

  async getImagesByAssociationId(
    associationId: string,
    associationType: string
  ): Promise<JNImage[]> {
    try {
      const response = await axios.get(
        `${this.engageOrigin}/api/v1/images/${associationType}/${associationId}?size=200`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      if (!response.data.success) return [];

      const images = response.data.images_data_array.map((image_data) => ({
        jnid: image_data.jnid,
        url: `${this.api1Origin}/api1/files/${image_data.jnid}`,
        size: image_data.size,
      }));

      return [...images];
    } catch (error) {
      return [];
    }
  }

  async fetchSignedImageUrls(images: JNImage[]): Promise<JNImage[]> {
    if (!images.length) return [];

    const jnidObjects = images.map((image) => ({ jnid: image.jnid }));
    const data = JSON.stringify(jnidObjects);

    try {
      const response = await axios.post(
        `${this.engageOrigin}/api/v1/images/get_signed_image_urls`,
        data,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      if (!response.data.success) return [];

      const signedUrls = response.data.signed_image_data.map((image_data) => ({
        jnid: image_data.id,
        url: image_data.url,
      }));

      return [...signedUrls];
    } catch (error) {
      return [];
    }
  }

  async savePhotoByUrlToContact(contactId: string, photoUrl: string) {
    try {
      const response = await axios.post(
        `${this.engageOrigin}/api/v1/images/contact/${contactId}`,
        {
          image_url: photoUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      if (!response.data.success) return null;

      return response.data.jnid;
    } catch (error) {
      return null;
    }
  }

  async savePhotoByUrlToJob(jobId: string, photoUrl: string) {
    try {
      const response = await axios.post(
        `${this.engageOrigin}/api/v1/images/job/${jobId}`,
        {
          image_url: photoUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      if (!response.data.success) return null;

      return response.data.jnid;
    } catch (error) {
      return null;
    }
  }

  async getTwilioPhotoContents(photoUrl: string) {
    try {
      const response = await fetch(
        `${this.engageOrigin}/api/v1/images/twilio`,
        {
          method: 'post',
          body: JSON.stringify({
            image_url: photoUrl,
          }),
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      const blob = await response.blob();

      return blob;
    } catch (error) {
      return null;
    }
  }
}
